import { JobT } from '@/utils/jobs/types';
import { useMutation, useQuery } from '@tanstack/react-query';
import { create } from 'zustand';
import { useAlgo } from './useAlgo';

export const useJobsStore = create((set) => ({
  availableJobs: [],
  setAvailableJobs: (jobs: JobT[]) => set({ availableJobs: jobs }),
}));

export const useJobsQuery = () => {
  // @ts-ignore
  const { address } = useAlgo();
  return useQuery({
    queryKey: ['jobs', address],
    enabled: !!address,
    queryFn: async () => {
      if (!address) {
        return [];
      }
      const response = await fetch(`/api/jobs?address=${address}`);
      const data = await response.json();
      return (data.results || []) as JobT[];
    },
  });
};

export const useGenerateJobsMutation = ({ onSuccess, onError }: { onSuccess?: () => void; onError?: (error: Error) => void } = {}) => {
  // @ts-ignore
  const { address, optInForJobGenerate } = useAlgo();
  return useMutation({
    mutationFn: async () => {
      if (!address) {
        throw new Error('Wallet not connected');
      }

      if (!optInForJobGenerate) {
        throw new Error('optInForJobGenerate function not available');
      }

      // Create opt-in transaction for Shitty Treats with verification note
      const txnId = await optInForJobGenerate();

      if (!txnId) {
        throw new Error('Failed to create verification transaction');
      }

      const response = await fetch('/api/jobs/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: process.env.NEXT_PUBLIC_API_KEY || '',
        },
        body: JSON.stringify({ address, txn: txnId }),
      });
      const data = await response.json();
      if (data.success) {
        return data.jobs;
      }
      const error = new Error(data.error);
      // @ts-ignore - Add cooldown information to error
      error.cooldownEndsAt = data.cooldownEndsAt;
      throw error;
    },
    onSuccess,
    onError,
  });
};

export const useStartJobMutation = ({ onSuccess, onError }: { onSuccess?: (job: JobT) => void; onError?: (error: Error) => void } = {}) => {
  // @ts-ignore
  const { address, optInForJobStart } = useAlgo();
  return useMutation({
    mutationFn: async (jobId: string) => {
      if (!address) {
        throw new Error('Wallet not connected');
      }

      if (!jobId) {
        throw new Error('Job ID is required');
      }

      if (!optInForJobStart) {
        throw new Error('optInForJobStart function not available');
      }

      // Create opt-in transaction for Shitty Treats with verification note
      const txnId = await optInForJobStart(jobId);

      if (!txnId) {
        throw new Error('Failed to create verification transaction');
      }

      const response = await fetch('/api/jobs/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: process.env.NEXT_PUBLIC_API_KEY || '',
        },
        body: JSON.stringify({ address, jobId, txn: txnId }),
      });
      const data = await response.json();
      if (data.success) {
        return data.job;
      }
      throw new Error(data.error);
    },
    onSuccess,
    onError,
  });
};

export const useClaimJobMutation = ({ onSuccess, onError }: { onSuccess?: (data: any) => void; onError?: (error: Error) => void } = {}) => {
  // @ts-ignore
  const { address } = useAlgo();
  return useMutation({
    mutationFn: async ({ jobId, txn }: { jobId: string; txn: string }) => {
      const response = await fetch('/api/jobs/claim', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: process.env.NEXT_PUBLIC_API_KEY || '',
        },
        body: JSON.stringify({ jobId, address, txn }),
      });
      const data = await response.json();
      if (data.success) {
        return data;
      }
      throw new Error(data.error);
    },
    onSuccess,
    onError,
  });
};

export const useRateJobMutation = ({ onSuccess, onError }: { onSuccess?: (data: any) => void; onError?: (error: Error) => void } = {}) => {
  // @ts-ignore
  const { address } = useAlgo();
  return useMutation({
    mutationFn: async ({ jobId, rating, comment }: { jobId: string; rating: number; comment?: string }) => {
      const response = await fetch('/api/jobs/rate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: process.env.NEXT_PUBLIC_API_KEY || '',
        },
        body: JSON.stringify({ jobId, address, rating, comment }),
      });
      const data = await response.json();
      if (data.success) {
        return data;
      }
      throw new Error(data.error);
    },
    onSuccess,
    onError,
  });
};
